import React, { useState, useCallback } from 'react'
import { VideoExtensionRequestRecord } from '@/api/videoApi'
import {
  useAdminExtensionRequestsInfinite,
  useApproveExtensionRequest,
  useRejectExtensionRequest,
} from '@/hooks/querry_hooks/common/useExtensionRequestsQuery'
import { useResponsiveColumns, VIDEO_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns'
import { useInfiniteScroll } from '@/hooks/common/useInfiniteScroll'
import ExtensionRequestDetailModal from '@/components/common/ExtensionRequestDetailModal'
import ExtensionRequestVideoTile from '@/components/common/ExtensionRequestVideoTile'
import ExtensionRequestsGrid from '@/components/common/ExtensionRequestsGrid'
import HourglassEmpty from '@mui/icons-material/HourglassEmpty'
import Refresh from '@mui/icons-material/Refresh'

const VideoExtensionRequestsPanel: React.FC<{
  filterUser?: string
  filterStatus?: string
  onViewExtended?: (videoId: string) => void
}> = ({ filterUser = 'all', filterStatus = 'all', onViewExtended }) => {
  const [rejectFeedback, setRejectFeedback] = useState<Record<string, string>>({})
  const [showRejectInput, setShowRejectInput] = useState<Record<string, boolean>>({})
  const [selectedReq, setSelectedReq] = useState<VideoExtensionRequestRecord | null>(null)
  const [viewLoadingId, setViewLoadingId] = useState<string | null>(null)
  const columns = useResponsiveColumns(VIDEO_GRID_COLUMNS)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useAdminExtensionRequestsInfinite({ filterUser, filterStatus, isAdmin: true })

  const approveMutation = useApproveExtensionRequest(filterUser, filterStatus)
  const rejectMutation = useRejectExtensionRequest(filterUser, filterStatus)

  const items = data?.pages.flatMap(p => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0
  const isLoading = isFetching && !isFetchingNextPage
  const allLoaded = !hasNextPage && items.length > 0

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetching,
    fetchNextPage,
  })

  const handleApprove = (id: string) => {
    approveMutation.mutate(id)
  }

  const handleReject = (id: string) => {
    rejectMutation.mutate(
      { id, feedback: rejectFeedback[id] },
      { onSuccess: () => setShowRejectInput(prev => ({ ...prev, [id]: false })) }
    )
  }

  const actionLoading = (id: string) =>
    (approveMutation.isPending && approveMutation.variables === id) ||
    (rejectMutation.isPending && rejectMutation.variables?.id === id)

  const handleViewExtended = useCallback((videoId: string) => {
    setViewLoadingId(videoId)
    try {
      onViewExtended?.(videoId)
    } finally {
      // Reset loading state after a short delay to show feedback
      setTimeout(() => setViewLoadingId(null), 500)
    }
  }, [onViewExtended])

  const pendingCount = items.filter(r => r.status === 'pending').length

  const renderItem = useCallback((req: VideoExtensionRequestRecord) => (
    <ExtensionRequestVideoTile
      req={req}
      isAdmin
      showUserEmail
      actionLoading={actionLoading(req.id)}
      showRejectInput={!!showRejectInput[req.id]}
      rejectFeedback={rejectFeedback[req.id] ?? ''}
      onApprove={() => handleApprove(req.id)}
      onReject={() => handleReject(req.id)}
      onShowReject={show => setShowRejectInput(prev => ({ ...prev, [req.id]: show }))}
      onFeedbackChange={val => setRejectFeedback(prev => ({ ...prev, [req.id]: val }))}
      onViewExtended={req.extended_video_id ? () => handleViewExtended(req.extended_video_id!) : undefined}
      viewLoading={viewLoadingId === req.extended_video_id}
      onTileClick={() => setSelectedReq(req)}
    />
  ), [showRejectInput, rejectFeedback, approveMutation.isPending, approveMutation.variables, rejectMutation.isPending, rejectMutation.variables, handleViewExtended, viewLoadingId])

  const getItemKey = useCallback((req: VideoExtensionRequestRecord) => req.id, [])

  return (
    <>
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Video Extension Requests</h2>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold text-white rounded-full" style={{ background: 'linear-gradient(to right, #F69A7E, #939BEC)' }}>
              {pendingCount}
            </span>
          )}
          {total > 0 && <span className="text-xs text-gray-500">({total} total)</span>}
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
        >
          <Refresh style={{ fontSize: '16px' }} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <ExtensionRequestsGrid
        items={items}
        columns={columns}
        renderItem={renderItem}
        getItemKey={getItemKey}
        sentinelRef={sentinelRef}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        isError={isError}
        allLoaded={allLoaded}
        emptyIcon={<HourglassEmpty style={{ fontSize: '40px' }} className="mb-3 opacity-40" />}
        emptyMessage="No extension requests yet"
        errorMessage="Failed to load extension requests"
      />
    </div>

    {selectedReq && (
      <ExtensionRequestDetailModal
        req={selectedReq}
        onClose={() => setSelectedReq(null)}
        isAdmin
        actionLoading={actionLoading(selectedReq.id)}
        onApprove={() => {
          handleApprove(selectedReq.id)
          setSelectedReq(null)
        }}
        onReject={(feedback) => {
          rejectMutation.mutate(
            { id: selectedReq.id, feedback },
            { onSuccess: () => setSelectedReq(null) }
          )
        }}
        onViewExtended={onViewExtended}
      />
    )}
  </>
  )
}

export default VideoExtensionRequestsPanel
